import { useRoom } from '@daily-co/daily-react'
import { addBreadcrumb, captureMessage } from '@sentry/react'
import { useState, useEffect, useMemo, useRef } from 'react'
import { shallowEqual } from 'react-redux'
import { useSelector } from 'store/hooks/useSelector'
import { useCallIsActive } from './useCallIsActive'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import { type UseDailyDisconnectMethodsReturn } from './useDailyDisconnectMethods'
import { classifyRingWindow, remainingRingMs } from './ringWindow'

/**
 * Everything `classifyRingWindow` needs, read in one pass.
 *
 * All three come from the same `POST /visits/{id}/video` response and are only meaningful
 * together — `videoCallToken` carries the server's `iat`, which is how the ring window gets
 * measured without a client clock (see `ringWindow.ts`).
 */
const selectRingWindowInputs = (state: Store) => ({
  videoCallTimeout: state.waitingRoomReducer.videoCallTimeout,
  videoCallTimeoutReceivedAtMs: state.waitingRoomReducer.videoCallTimeoutReceivedAtMs,
  videoCallToken: state.waitingRoomReducer.videoCallToken,
})

export function useCallTimedOut({ endVisitCall }: Pick<UseDailyDisconnectMethodsReturn, 'endVisitCall'>) {
  // `shallowEqual` because react-redux defaults to reference equality, which the fresh object
  // literal above never satisfies — without it this re-renders on every dispatched action.
  const { videoCallTimeout, videoCallTimeoutReceivedAtMs, videoCallToken } = useSelector(
    selectRingWindowInputs,
    shallowEqual,
  )

  const room = useRoom()
  const hasRoom = !!room

  // Never start out timed out. The hook mounts with `DailyVisitProvider`, long before any call
  // begins, so a truthy `videoCallTimeout` at mount describes a call that is already over — it
  // used to be rehydrated from localStorage, and treating it as a timeout fired `endVisitCall()`
  // (and a stale video DELETE) on page load. `stripInVisitRoom` now closes that particular route,
  // but the initialiser stays `false` so no future writer can reopen it. The effects below own
  // this from here (QMD-4640).
  const [callTimedOut, setCallTimedOut] = useState(false)

  /**
   * How long to ring, and whether the server's window could be measured at all.
   *
   * Memoised on the response's own fields rather than recomputed per effect run, so the timer and
   * the Sentry report below cannot reach different conclusions about the same call. The verdict is
   * time-invariant by construction — see `ringWindow.ts`.
   */
  const ringWindow = useMemo(
    () =>
      videoCallTimeout ?
        classifyRingWindow({
          expiresAtIso: videoCallTimeout,
          accessToken: videoCallToken ?? null,
          // `?? null` is load-bearing: the reducer reads this off an untyped legacy
          // `action.extraData`, so a dispatcher that omits it writes `undefined` despite the type.
          receivedAtMs: videoCallTimeoutReceivedAtMs ?? null,
          nowMs: Date.now(),
        })
      : null,
    [videoCallTimeout, videoCallTimeoutReceivedAtMs, videoCallToken],
  )

  const patientConnectedTimerRef = useRef<NodeJS.Timeout | null>(null)
  // Tracks whether the patient connected during the *current* call attempt. Used to suppress
  // the "patient never connected" timeout firing on the post-hangup re-evaluation, which
  // would otherwise surface "No answer" on a call that actually connected. Reset below when
  // the ring window changes (= a new call attempt has been initiated).
  const patientConnectedThisAttemptRef = useRef(false)

  const { callIsActive } = useCallIsActive()

  // A new ring window means a fresh call attempt — clear the "already connected" flag
  // so a second attempt within the same popup session can still time out if unanswered.
  useEffect(() => {
    patientConnectedThisAttemptRef.current = false
  }, [ringWindow])

  // Reset callTimedOut when room is cleared, connection established, or visit is being restarted
  useEffect(() => {
    if (!ringWindow || callIsActive) {
      if (callIsActive) patientConnectedThisAttemptRef.current = true
      if (patientConnectedTimerRef.current !== null) clearTimeout(patientConnectedTimerRef.current)
      setCallTimedOut(false)
    }
  }, [ringWindow, callIsActive])

  useEffect(() => {
    // Skip if the patient has already connected in this call attempt — the call ended
    // normally and should resolve to CONNECTED_CALL_ENDED, not FAILED.
    if (patientConnectedThisAttemptRef.current) return
    // Only set timeout if we have a room, ring window, and visit is not being restarted
    if (hasRoom && ringWindow && !callIsActive) {
      const remainingMs = remainingRingMs(ringWindow, Date.now())
      if (remainingMs > 0) {
        patientConnectedTimerRef.current = setTimeout(() => {
          setCallTimedOut(true)
        }, remainingMs)
      } else {
        setCallTimedOut(true)
      }
    }
    return () => {
      if (patientConnectedTimerRef.current !== null) {
        clearTimeout(patientConnectedTimerRef.current)
        patientConnectedTimerRef.current = null
      }
    }
  }, [hasRoom, ringWindow, callIsActive])

  /**
   * Report a ring window we could not measure, once per call attempt.
   *
   * This should be silent in production: every healthy call reads the window off the server's own
   * timestamps. An event here means the Daily token stopped carrying `iat`, or the backend changed
   * the window to something implausible — both of which degrade the provider to a flat 70s ring
   * and are worth knowing about before a patient notices.
   *
   * Once-per-attempt falls out of the data rather than needing a guard: every attempt begins with
   * `setProviderInitiatingVisit(true)`, which nulls the expiry pair, so this effect sees
   * `null -> value` exactly once. There is no `StrictMode` double-invoke in `main.tsx`, and
   * `START_VISIT_VIDEO_CALL_SUCCESS` is in the cross-tab sync blacklist, so no sibling tab
   * duplicates it.
   */
  useEffect(() => {
    if (!ringWindow || ringWindow.verdict === 'server-issued') return
    const data = {
      verdict: ringWindow.verdict,
      issuedWindowMs: ringWindow.issuedWindowMs,
      windowMs: ringWindow.windowMs,
    }
    addBreadcrumb({ category: 'clock', level: 'warning', message: `ring window: ${ringWindow.verdict}`, data })
    captureMessage(`video call ring window not server-measured: ${ringWindow.verdict}`, {
      level: 'warning',
      tags: { ring_window_verdict: ringWindow.verdict },
      extra: data,
    })
  }, [ringWindow])

  const endVisitCallRef = useAutoUpdatingRef(endVisitCall)

  useEffect(() => {
    if (callTimedOut) endVisitCallRef.current?.()
  }, [callTimedOut, endVisitCallRef])

  return { callTimedOut }
}
